// Cross-cutting helpers for the /karty hub: contextual product CTAs, spread
// recommendations per card, and the deterministic "card of the day" pick.
// Kept framework-free so screens + hubs share one source of truth.
import { SPREAD_SEO_DATA, type SpreadSeoData } from '@app/data/spreadSeoData';
import { MAJOR_ARCANA_SLUGS, MINOR_ARCANA_SLUGS, type CardSeoData } from '@app/data/cardSeoData';
import { APP_CTA_TO, TELEGRAM_BOT_BASE_URL } from '@app/presentation/components/site/constants';

// Canonical 78-slot deck order (Major 0–XXI, then Кубки/Пентакли/Мечи/Жезлы). Its
// index is the UNIVERSAL join key across surfaces: zero-padded it equals the backend
// TarotCardSchema.key ("00".."77") and the SEO card-face basename (cards/NN.jpg).
// Verified 0/78 name mismatches between this order and the backend deck. The web
// /karty data is keyed by RU slug; the in-app/backend card identity is keyed by this
// index — bridge between them via deckKeyForSlug / slugForDeckKey, never by free text.
export const DECK_ORDER: string[] = [...MAJOR_ARCANA_SLUGS, ...MINOR_ARCANA_SLUGS];

/** Zero-padded deck key ("00".."77") for a /karty slug, or null if unknown. */
export const deckKeyForSlug = (slug: string): string | null => {
  const i = DECK_ORDER.indexOf(slug);
  return i < 0 ? null : String(i).padStart(2, '0');
};

/** /karty slug for a backend card key ("00".."77") or numeric id, or null. */
export const slugForDeckKey = (key: string | number): string | null => {
  const i = typeof key === 'number' ? key : Number.parseInt(key, 10);
  return Number.isInteger(i) && i >= 0 && i < DECK_ORDER.length ? DECK_ORDER[i] : null;
};

// Suit → the most thematically relevant existing /rasklady spread.
const SPREAD_FOR_SUIT: Record<string, string> = {
  kubki: 'lyubovnyj-rasklad', // вода / чувства
  pentakli: 'finansovyj-rasklad', // земля / деньги
  mechi: 'gadanie-na-situaciyu', // воздух / ситуация
  zhezly: 'rasklad-na-talant', // огонь / действие
};

export const recommendedSpreadSlug = (card: CardSeoData): string =>
  card.arcana === 'major'
    ? 'rasklad-sudby'
    : (card.suit && SPREAD_FOR_SUIT[card.suit]) || 'rasklad-na-tri-karty';

export const recommendedSpread = (card: CardSeoData): SpreadSeoData =>
  SPREAD_SEO_DATA[recommendedSpreadSlug(card)];

// Contextual product CTAs. The card context rides along as a query/start-payload;
// harmless if the app/bot doesn't consume it yet, but ready when it does.
export const cardAppCta = (slug: string): string => `${APP_CTA_TO}?from=card&card=${slug}`;
export const cardTelegramDeepLink = (slug: string): string =>
  `${TELEGRAM_BOT_BASE_URL}?start=card_${slug.replace(/-/g, '_')}`;

// "Ask the cat" preset questions per card — shown on the page, deep-linked into the bot.
export const askTheCatQuestions = (card: CardSeoData): Array<{ label: string; href: string }> => {
  const n = card.name;
  const presets = [
    `Что ${n} говорит о моих отношениях?`,
    `Почему мне выпала карта ${n}?`,
    `Что ${n} советует мне сейчас?`,
  ];
  return presets.map((label) => ({ label, href: cardTelegramDeepLink(card.slug) }));
};

// Deterministic "card of the day": same card for everyone on a given date, no backend.
// IMPORTANT: callers must compute `dateStr` CLIENT-SIDE (onMounted) so the SSG-rendered
// HTML isn't frozen to the build date (which would cause a hydration mismatch).
export const cardOfDaySlug = (dateStr: string, order: string[]): string => {
  let h = 0;
  for (let i = 0; i < dateStr.length; i++) h = (h * 31 + dateStr.charCodeAt(i)) >>> 0;
  return order[h % order.length];
};

// YYYY-MM-DD in local time (client-side helper).
export const todayKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
